import { defineMessages } from 'react-intl';

import type {
  cardStatKeys,
  decisiveStatKeys,
  negativeDecisiveStatKeys,
  positiveDecisiveStatKeys,
  scoutingStatKeys,
} from './stats.ts';

export const categoryLabels = defineMessages({
  GENERAL: {
    defaultMessage: 'general',
  },
  DEFENDING: {
    defaultMessage: 'defending',
  },
  POSSESSION: {
    defaultMessage: 'possession',
  },
  PASSING: {
    defaultMessage: 'passing',
  },
  ATTACKING: {
    defaultMessage: 'attacking',
  },
  GOALKEEPING: {
    defaultMessage: 'goalkeeping',
  },
  HITTING: {
    defaultMessage: 'hitting',
  },
  PITCHING: {
    defaultMessage: 'pitching',
  },
  POSITIVE_DECISIVE_STAT: {
    defaultMessage: 'Positive decisive',
  },
  NEGATIVE_DECISIVE_STAT: {
    defaultMessage: 'negative decisive',
  },
});

export type PositiveDecisiveStatKey = (typeof positiveDecisiveStatKeys)[number];

export type NegativeDecisiveStatKey = (typeof negativeDecisiveStatKeys)[number];

export const statLabels = defineMessages({
  goals: {
    defaultMessage: 'goal',
  },
  goal_assist: {
    defaultMessage: 'goal assist',
  },
  mins_played: {
    defaultMessage: 'mins played',
  },
  total_pass: {
    defaultMessage: 'total pass',
  },
  accurate_pass: {
    defaultMessage: 'accurate pass',
  },
  yellow_card: {
    defaultMessage: 'yellow card',
  },
  red_card: {
    defaultMessage: 'red card',
  },
  clean_sheet: {
    defaultMessage: 'clean sheet',
  },
  ontarget_scoring_att: {
    defaultMessage: 'shot on goal',
  },
  adjusted_ontarget_scoring_att: {
    defaultMessage: 'shot on goal',
  },
  blocked_scoring_att: {
    defaultMessage: 'blocked shot',
  },
  total_tackle: {
    defaultMessage: 'total tackle',
  },
  parries: {
    defaultMessage: 'parry',
  },
  was_fouled: {
    defaultMessage: 'was fouled',
  },
  penalty_kick_missed: {
    defaultMessage: 'penalty kick missed',
  },
  fouls: {
    defaultMessage: 'foul',
  },
  penalty_save: {
    defaultMessage: 'penalty saved',
  },
  own_goals: {
    defaultMessage: 'own goal',
  },
  total_scoring_att: {
    defaultMessage: 'total scoring attempt',
  },
  lost_corners: {
    defaultMessage: 'lost corner',
  },
  goals_conceded: {
    defaultMessage: 'goal conceded',
  },
  three_goals_conceded: {
    defaultMessage: '3 goals conceded',
  },
  saves: {
    defaultMessage: 'saves',
  },
  goal_kicks: {
    defaultMessage: 'goal kick',
  },
  game_started: {
    defaultMessage: 'game started',
  },
  total_clearance: {
    defaultMessage: 'total clearance',
  },
  formation_place: {
    defaultMessage: 'formation place',
  },
  single_goal_game: {
    defaultMessage: 'single goal game',
  },
  error_lead_to_goal: {
    defaultMessage: 'error lead to goal',
  },
  error_lead_to_shot: {
    defaultMessage: 'error leading to shot',
  },
  clean_sheet_60: {
    defaultMessage: 'clean sheet 60',
  },
  outfielder_block: {
    defaultMessage: 'outfielder block',
  },
  blocked_cross: {
    defaultMessage: 'blocked cross',
  },
  last_man_tackle: {
    defaultMessage: 'last man tackle',
  },
  interception_won: {
    defaultMessage: 'interception won',
  },
  clearance_off_line: {
    defaultMessage: 'clearance off line',
  },
  effective_clearance: {
    defaultMessage: 'effective clearance',
  },
  won_tackle: {
    defaultMessage: 'won tackle',
  },
  lost_tackle: {
    defaultMessage: 'lost tackle',
  },
  touches: {
    defaultMessage: 'touches',
  },
  poss_won: {
    defaultMessage: 'possession won',
  },
  poss_lost_ctrl: {
    defaultMessage: 'possession lost',
  },
  duel_won: {
    defaultMessage: 'duel won',
  },
  duel_lost: {
    defaultMessage: 'duel lost',
  },
  net_duel: {
    defaultMessage: 'net duel',
  },
  adjusted_goal_assist: {
    defaultMessage: 'adjusted goal assist',
  },
  adjusted_total_att_assist: {
    defaultMessage: 'attempted assist',
  },
  adjusted_accurate_fwd_zone_pass: {
    defaultMessage: 'accurate forward pass',
  },
  successful_final_third_passes: {
    defaultMessage: 'successful final third pass',
  },
  accurate_through_ball: {
    defaultMessage: 'accurate through ball',
  },
  pen_area_entries: {
    defaultMessage: 'penalty area entry',
  },
  missed_pass: {
    defaultMessage: 'missed pass',
  },
  accurate_long_balls: {
    defaultMessage: 'accurate long ball',
  },
  accurate_cross: {
    defaultMessage: 'accurate cross',
  },
  assist_penalty_won: {
    defaultMessage: 'penalty assist',
  },
  adjusted_goals: {
    defaultMessage: 'adjusted goal',
  },
  post_scoring_att: {
    defaultMessage: 'post scoring attempt',
  },
  won_contest: {
    defaultMessage: 'won contest',
  },
  lost_contest: {
    defaultMessage: 'lost contest',
  },
  big_chance_missed: {
    defaultMessage: 'big chance missed',
  },
  big_chance_created: {
    defaultMessage: 'big chance created',
  },
  double_double: {
    defaultMessage: 'double double',
  },
  triple_double: {
    defaultMessage: 'triple double',
  },
  triple_triple: {
    defaultMessage: 'triple triple',
  },
  possession_bonus: {
    defaultMessage: 'possession bonus',
  },
  penalty_conceded: {
    defaultMessage: 'penalty conceded',
  },
  penalties_saved: {
    defaultMessage: 'penalty saved',
  },
  saved_ibox: {
    defaultMessage: 'save in the box',
  },
  saved_obox: {
    defaultMessage: 'save outside the box',
  },
  good_high_claim: {
    defaultMessage: 'good high claim',
  },
  punches: {
    defaultMessage: 'punches',
  },
  dive_save: {
    defaultMessage: 'dive save',
  },
  dive_catch: {
    defaultMessage: 'dive catch',
  },
  cross_not_claimed: {
    defaultMessage: 'cross not claimed',
  },
  gk_smother: {
    defaultMessage: 'gk smother',
  },
  keeper_pick_up: {
    defaultMessage: 'keeper pick up',
  },
  accurate_keeper_sweeper: {
    defaultMessage: 'accurate keeper sweeper',
  },
  accurate_goal_kicks: {
    defaultMessage: 'accurate goal kick',
  },
  long_pass_own_to_opp_success: {
    defaultMessage: 'Long pass into opposition',
  },
  six_second_violation: {
    defaultMessage: 'six second violation',
  },
  points: {
    defaultMessage: 'Points',
  },
  rebounds: {
    defaultMessage: 'Rebounds',
  },
  assists: {
    defaultMessage: 'Assists',
  },
  blocks: {
    defaultMessage: 'Blocks',
  },
  steals: {
    defaultMessage: 'Steals',
  },
  turnovers: {
    defaultMessage: 'Turnovers',
  },
  three_points_made: {
    defaultMessage: 'Made 3-point FGs',
  },
  set_piece_taken: {
    defaultMessage: 'set piece taken',
  },
  hitting_at_base: {
    defaultMessage: 'At base',
  },
  hitting_at_plate: {
    defaultMessage: 'At plate',
  },
  hitting_caught_stealing: {
    defaultMessage: 'Caught stealing',
  },
  hitting_doubles: {
    defaultMessage: 'Doubles',
  },
  hitting_hit_by_pitch: {
    defaultMessage: 'Hit by pitch',
  },
  hitting_home_runs: {
    defaultMessage: 'Home runs',
  },
  hitting_runs: {
    defaultMessage: 'Runs',
  },
  hitting_runs_batted_in: {
    defaultMessage: 'Runs batted in',
  },
  hitting_singles: {
    defaultMessage: 'Singles',
  },
  hitting_stolen_bases: {
    defaultMessage: 'Stolen bases',
  },
  hitting_strikeouts: {
    defaultMessage: 'Strikeouts',
  },
  hitting_triples: {
    defaultMessage: 'Triples',
  },
  hitting_walks: {
    defaultMessage: 'Walks',
  },
  penalty_won: {
    defaultMessage: 'Penalty Won',
  },
  pitching_earned_runs: {
    defaultMessage: 'Earned Runs',
  },
  pitching_hits_allowed: {
    defaultMessage: 'Hits Allowed',
  },
  pitching_hit_batsmen: {
    defaultMessage: 'Hit Batsmen',
  },
  pitching_holds: {
    defaultMessage: 'Holds',
  },
  pitching_innings_pitched: {
    defaultMessage: 'Innings Pitched',
  },
  pitching_no_hitters: {
    defaultMessage: 'No Hitters',
  },
  pitching_relief_appearance: {
    defaultMessage: 'Relief Appearance',
  },
  pitching_saves: {
    defaultMessage: 'Saves',
  },
  pitching_strikeouts: {
    defaultMessage: 'Strikeouts',
  },
  pitching_walks: {
    defaultMessage: 'Walks',
  },
  pitching_wins: {
    defaultMessage: 'Wins',
  },
});

export type Stats =
  | (typeof decisiveStatKeys)[number]
  | (typeof scoutingStatKeys)[number]
  | (typeof cardStatKeys)[number];
